'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import {
  canApplySharedRouteDescription,
  canApplySharedRouteTitle,
  formatDocumentTitle,
  getRouteMetadata,
} from '@/app/lib/routeMetadata';

export const DESCRIPTION_META_SELECTOR = 'meta[name="description"]';

export function applyRouteMetadata(
  pathname: string,
  previousSharedTitle: string | null = null,
  previousPageTitle: string | null = null,
  previousSharedDescription: string | null = null,
  previousPageDescription: string | null = null,
): { title: string | null; description: string | null } {
  const metadata = getRouteMetadata(pathname);
  let appliedTitle: string | null = null;
  if (canApplySharedRouteTitle(document.title, metadata.title, previousSharedTitle, previousPageTitle)) {
    appliedTitle = formatDocumentTitle(metadata.title);
    document.title = appliedTitle;
  }

  let description = document.head.querySelector<HTMLMetaElement>(DESCRIPTION_META_SELECTOR);
  if (!description) {
    description = document.createElement('meta');
    description.name = 'description';
    document.head.appendChild(description);
  }
  let appliedDescription: string | null = null;
  if (canApplySharedRouteDescription(
    description.content,
    metadata.description,
    previousSharedDescription,
    previousPageDescription,
  )) {
    description.content = metadata.description;
    appliedDescription = metadata.description;
  }
  return { title: appliedTitle, description: appliedDescription };
}

export default function RouteMetadataManager() {
  const pathname = usePathname();
  const currentPathname = pathname || '/';
  const lastSharedTitle = useRef<string | null>(null);
  const lastSharedDescription = useRef<string | null>(null);
  const appliedPathname = useRef<string | null>(null);
  const latestPathname = useRef(currentPathname);
  const titleByPathname = useRef(new Map<string, string>());
  const descriptionByPathname = useRef(new Map<string, string>());

  // Update before passive-effect cleanup so a title mutation from the new
  // route is never recorded as belonging to the route being left.
  useLayoutEffect(() => {
    latestPathname.current = currentPathname;
  }, [currentPathname]);

  useEffect(() => {
    const previousPathname = appliedPathname.current;
    const previousPageTitle = previousPathname && previousPathname !== currentPathname
      ? titleByPathname.current.get(previousPathname) ?? null
      : null;
    const previousPageDescription = previousPathname && previousPathname !== currentPathname
      ? descriptionByPathname.current.get(previousPathname) ?? null
      : null;

    const applied = applyRouteMetadata(
      currentPathname,
      lastSharedTitle.current,
      previousPageTitle,
      lastSharedDescription.current,
      previousPageDescription,
    );
    if (applied.title) lastSharedTitle.current = applied.title;
    if (applied.description) lastSharedDescription.current = applied.description;
    appliedPathname.current = currentPathname;
    titleByPathname.current.set(currentPathname, document.title);
    const currentDescription = document.head.querySelector<HTMLMetaElement>(DESCRIPTION_META_SELECTOR)?.content;
    if (currentDescription !== undefined) {
      descriptionByPathname.current.set(currentPathname, currentDescription);
    }

    const observer = new MutationObserver(() => {
      if (latestPathname.current === currentPathname) {
        titleByPathname.current.set(currentPathname, document.title);
        const description = document.head.querySelector<HTMLMetaElement>(DESCRIPTION_META_SELECTOR)?.content;
        if (description !== undefined) {
          descriptionByPathname.current.set(currentPathname, description);
        }
      }
    });
    observer.observe(document.head, {
      attributes: true,
      attributeFilter: ['content'],
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, [currentPathname]);

  return null;
}
